<template>
  <div class="max-w-7xl mx-auto px-4 py-6">
    <div class="flex items-center justify-between mb-8">
      <h1 class="text-3xl font-bold text-gray-800">Cutting Optimizer</h1>
      <div class="bg-blue-100 text-blue-800 rounded-lg px-4 py-2">
        <span class="font-medium">Material Optimization Tool</span>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div class="lg:col-span-1 space-y-6">
        <!-- Material Configuration Card -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100">
          <div class="px-6 py-4 border-b border-gray-100">
            <h2 class="text-xl font-semibold text-gray-800 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-blue-500 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                  clip-rule="evenodd"
                />
              </svg>
              Configure Material
            </h2>
          </div>
          <div class="p-6">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Width</label
                >
                <div class="relative">
                  <input
                    type="number"
                    class="block w-full px-4 py-2 rounded-md border border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition"
                    v-model.number="material.width"
                    min="1"
                  />
                  <div
                    class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 text-sm"
                  >
                    units
                  </div>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1"
                  >Height</label
                >
                <div class="relative">
                  <input
                    type="number"
                    class="block w-full px-4 py-2 rounded-md border border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition"
                    v-model.number="material.height"
                    min="1"
                  />
                  <div
                    class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 text-sm"
                  >
                    units
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Parts Card -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100">
          <div
            class="px-6 py-4 border-b border-gray-100 flex justify-between items-center"
          >
            <h2 class="text-xl font-semibold text-gray-800 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-blue-500 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
              Parts
            </h2>
            <button
              class="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              @click="
                addPart();
                trackButtonClick('Add Part', 'interaction');
              "
            >
              <svg
                class="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                ></path>
              </svg>
              Add Part
            </button>
          </div>
          <div class="p-6">
            <div v-if="parts.length === 0" class="text-center py-8">
              <svg
                class="mx-auto h-12 w-12 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1"
                  d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                ></path>
              </svg>
              <p class="mt-2 text-sm text-gray-500">No parts added yet</p>
            </div>
            <div class="space-y-4">
              <div
                v-for="(part, index) in parts"
                :key="index"
                class="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-200 transition-all"
              >
                <div class="grid grid-cols-3 gap-3">
                  <div>
                    <label class="block text-xs font-medium text-gray-500 mb-1"
                      >Width</label
                    >
                    <input
                      type="number"
                      class="block w-full px-3 py-1.5 rounded border border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition text-sm"
                      v-model.number="part.width"
                      min="1"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-500 mb-1"
                      >Height</label
                    >
                    <input
                      type="number"
                      class="block w-full px-3 py-1.5 rounded border border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition text-sm"
                      v-model.number="part.height"
                      min="1"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-500 mb-1"
                      >Quantity</label
                    >
                    <input
                      type="number"
                      class="block w-full px-3 py-1.5 rounded border border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition text-sm"
                      v-model.number="part.quantity"
                      min="1"
                    />
                  </div>
                </div>
                <div class="flex justify-end mt-3">
                  <button
                    class="inline-flex items-center px-2.5 py-1 bg-red-50 text-red-600 text-xs font-medium rounded hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    @click="
                      removePart(index);
                      trackButtonClick('Remove Part', 'interaction');
                    "
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3 w-3 mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Optimization Strategy Card -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100">
          <div class="px-6 py-4 border-b border-gray-100">
            <h2 class="text-xl font-semibold text-gray-800 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-blue-500 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                  clip-rule="evenodd"
                />
              </svg>
              Optimization Strategy
            </h2>
          </div>
          <div class="p-6">
            <div class="mb-5">
              <!-- <label class="block text-sm font-medium text-gray-700 mb-1"
                >Select Strategy</label
              >
              <select
                v-model="selectedStrategyId"
                class="block w-full px-4 py-2 rounded-md border border-gray-200 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition bg-white"
              >
                <option
                  v-for="strategy in availableStrategies"
                  :key="strategy.id"
                  :value="strategy.id"
                >
                  {{ strategy.name }}
                </option>
              </select> -->
            </div>

            <button
              class="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              @click="
                runOptimization();
                trackOptimization('run_optimization', selectedStrategyId);
              "
              :disabled="
                isOptimizing || parts.length === 0 || isComparingStrategies
              "
              :class="{
                'opacity-60 cursor-not-allowed':
                  isOptimizing || parts.length === 0 || isComparingStrategies,
              }"
            >
              <svg
                v-if="isOptimizing"
                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{ isOptimizing ? 'Optimizing...' : 'Run Optimization' }}
            </button>

            <!-- <button
              class="w-full inline-flex items-center justify-center px-6 py-3 mt-3 bg-emerald-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
              @click="runAllStrategiesComparison"
              :disabled="
                isOptimizing || parts.length === 0 || isComparingStrategies
              "
              :class="{
                'opacity-60 cursor-not-allowed':
                  isOptimizing || parts.length === 0 || isComparingStrategies,
              }"
            >
              <svg
                v-if="isComparingStrategies"
                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{
                isComparingStrategies
                  ? 'Comparing Strategies...'
                  : 'Compare All Strategies'
              }}
            </button> -->

            <div
              v-if="error"
              class="mt-4 bg-red-50 border border-red-200 text-red-600 rounded-md px-4 py-3 text-sm"
            >
              {{ error }}
            </div>
          </div>
        </div>
      </div>

      <div class="lg:col-span-2">
        <!-- Results Card -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100">
          <div class="px-6 py-4 border-b border-gray-100">
            <h2 class="text-xl font-semibold text-gray-800 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-blue-500 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V8z"
                  clip-rule="evenodd"
                />
              </svg>
              Results
            </h2>
          </div>
          <div class="p-6">
            <!-- Debug Strategy Tests Component -->
            <DebugStrategyTests v-if="showDebugTests" />

            <!-- Toggle Debug Tests -->
            <!-- <div class="flex justify-end mb-4">
              <button
                @click="toggleDebugTests"
                class="text-sm px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                {{ showDebugTests ? 'Hide Debug Tests' : 'Show Debug Tests' }}
              </button>
            </div> -->

            <div
              v-if="!result"
              class="flex flex-col items-center justify-center py-16"
            >
              <svg
                class="w-16 h-16 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1"
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                ></path>
              </svg>
              <p class="mt-4 text-gray-500">
                Configure your material and parts, then run the optimizer.
              </p>
            </div>
            <div v-else>
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div class="bg-blue-50 rounded-lg p-4 text-center">
                  <p class="text-sm text-blue-600 font-medium">Strategy</p>
                  <h4 class="text-lg font-semibold text-gray-800 mt-1">
                    {{ result.strategyName }}
                  </h4>
                </div>
                <div class="bg-green-50 rounded-lg p-4 text-center">
                  <p class="text-sm text-green-600 font-medium">Efficiency</p>
                  <h4 class="text-lg font-semibold text-gray-800 mt-1">
                    {{ (100 - result.wastePercentage).toFixed(2) }}%
                  </h4>
                </div>
                <div class="bg-purple-50 rounded-lg p-4 text-center">
                  <p class="text-sm text-purple-600 font-medium">
                    Materials Used
                  </p>
                  <h4 class="text-lg font-semibold text-gray-800 mt-1">
                    {{ result.usedMaterials.length }}
                  </h4>
                </div>
                <div class="bg-amber-50 rounded-lg p-4 text-center">
                  <p class="text-sm text-amber-600 font-medium">Waste</p>
                  <h4 class="text-lg font-semibold text-gray-800 mt-1">
                    {{ result.wastePercentage.toFixed(2) }}%
                  </h4>
                </div>
              </div>

              <div class="mb-6">
                <h3
                  class="text-lg font-semibold text-gray-800 mb-3 flex items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-blue-500 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z"
                    />
                  </svg>
                  Parts Summary
                </h3>
                <div
                  class="bg-white overflow-hidden border border-gray-200 rounded-lg"
                >
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Size (W×H)
                        </th>
                        <th
                          scope="col"
                          class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Requested
                        </th>
                        <th
                          scope="col"
                          class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Placed
                        </th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr
                        v-for="(part, i) in parts"
                        :key="i"
                        class="hover:bg-gray-50"
                      >
                        <td
                          class="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900"
                        >
                          {{ part.width }} × {{ part.height }}
                        </td>
                        <td
                          class="px-6 py-3 whitespace-nowrap text-sm text-gray-500"
                        >
                          {{ part.quantity }}
                        </td>
                        <td
                          class="px-6 py-3 whitespace-nowrap text-sm text-gray-500"
                        >
                          <span
                            :class="{
                              'text-green-600 font-medium':
                                countPlacedParts(part) === part.quantity,
                              'text-orange-500 font-medium':
                                countPlacedParts(part) < part.quantity,
                            }"
                          >
                            {{ countPlacedParts(part) }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="space-y-6">
                <div
                  v-for="(groupedMat, groupIndex) in groupedMaterials"
                  :key="groupIndex"
                  class="bg-gray-50 rounded-lg p-4 border border-gray-200"
                >
                  <div class="flex items-center justify-between mb-3">
                    <h3 class="text-lg font-medium text-gray-800">
                      Material Pattern #{{ groupIndex + 1 }}
                      <span
                        v-if="groupedMat.count > 1"
                        class="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                      >
                        × {{ groupedMat.count }}
                      </span>
                    </h3>
                    <div
                      v-if="groupedMat.count > 1"
                      class="text-sm text-gray-600"
                    >
                      Materials #{{ groupedMat.materialNumbers.join(', ') }}
                    </div>
                  </div>

                  <div
                    class="border border-gray-300 rounded bg-white relative mb-2 overflow-hidden"
                    :style="{
                      width: `${Math.min(
                        groupedMat.material.width * 4,
                        1200
                      )}px`,
                      height: `${Math.min(
                        groupedMat.material.height * 4,
                        800
                      )}px`,
                    }"
                  >
                    <div
                      v-for="(part, partIndex) in groupedMat.material.children"
                      :key="partIndex"
                      class="absolute border border-gray-400 shadow-sm transition-transform hover:z-10 hover:shadow-md"
                      :style="{
                        left: `${(part.x || 0) * 4}px`,
                        top: `${(part.y || 0) * 4}px`,
                        width: `${part.width * 4}px`,
                        height: `${part.height * 4}px`,
                        backgroundColor: getRandomColor(partIndex),
                      }"
                    >
                      <div
                        class="absolute inset-0 flex items-center justify-center"
                      >
                        <span
                          class="text-white text-xs font-medium px-1 py-0.5 bg-black bg-opacity-50 rounded"
                        >
                          {{ part.width }}×{{ part.height }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Parts count summary for this pattern -->
                  <div
                    v-if="groupedMat.count > 1"
                    class="mt-2 text-sm text-gray-600"
                  >
                    <span class="font-medium"
                      >Total parts in this pattern:</span
                    >
                    {{ groupedMat.material.children?.length || 0 }} parts ×
                    {{ groupedMat.count }} materials =
                    {{
                      (groupedMat.material.children?.length || 0) *
                      groupedMat.count
                    }}
                    total parts
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Debug Visualization for Landscape First Debug Strategy -->
        <DebugVisualization
          v-if="optimizationResult && showDebugTests"
          :result="optimizationResult"
          :scale="3"
        />

        <!-- No results message -->
        <div
          v-if="!optimizationResult && !isOptimizing"
          class="p-6 bg-white rounded-xl shadow-sm border border-gray-100"
        >
          <div class="text-center py-10">
            <div class="text-gray-400 mb-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-12 w-12 mx-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p class="text-gray-500">
              Configure your materials and click "Optimize" to see results
            </p>
          </div>
        </div>

        <!-- Strategy Comparison Results -->
        <div
          v-if="comparisonResults && comparisonResults.length > 0"
          class="p-6 bg-white rounded-xl shadow-sm border border-gray-100 mb-6"
        >
          <h2
            class="text-xl font-semibold text-gray-800 flex items-center mb-4"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-blue-500 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
              <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
            </svg>
            Strategy Comparison
          </h2>

          <div class="overflow-hidden border border-gray-200 rounded-lg mb-4">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Rank
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Strategy
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Waste %
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Materials Used
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr
                  v-for="(item, index) in comparisonResults"
                  :key="item.strategyId"
                  :class="{
                    'bg-green-50': index === 0,
                    'hover:bg-gray-50': index !== 0,
                  }"
                >
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <span
                      v-if="index === 0"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                    >
                      Best
                    </span>
                    <span v-else>{{ index + 1 }}</span>
                  </td>
                  <td
                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                  >
                    {{ item.strategyName }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ item.result.wastePercentage.toFixed(2) }}%
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ item.result.usedMaterials.length }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      @click="
                        selectComparisonResult(item);
                        trackButtonClick('Select Strategy', 'optimization', {
                          strategy: item.strategyName,
                        });
                      "
                      class="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Select
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-if="comparisonResults.length > 0"
            class="mt-3 text-sm text-gray-600"
          >
            <p>
              <span class="font-medium">Best strategy:</span>
              {{ comparisonResults[0].strategyName }} with
              {{ comparisonResults[0].result.wastePercentage.toFixed(2) }}%
              waste using
              {{ comparisonResults[0].result.usedMaterials.length }} materials.
            </p>
          </div>
        </div>

        <!-- Add this section to show unplaced parts in the template -->
        <div
          v-if="result && hasUnplacedPartsComputed"
          class="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4"
        >
          <h3 class="text-lg font-medium text-amber-800 mb-3 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-amber-600 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
            Unplaced Parts Summary
          </h3>
          <div class="text-sm text-amber-700">
            <p class="mb-2">
              Some parts could not be placed with the current material size.
            </p>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div
                v-for="(unplacedSummary, index) in unplacedPartsSummary"
                :key="index"
                class="bg-white rounded border border-amber-200 p-3"
              >
                <p class="font-medium">
                  {{ unplacedSummary.width }}×{{ unplacedSummary.height }}
                </p>
                <p class="text-xs">
                  {{ unplacedSummary.count }} piece(s) remaining
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue';
  import { useOptimizer } from '~/composables/useOptimizer';
  import type { IOrientationCompareResult } from '~/composables/strategies/OrientationCompareStrategy';
  import type { IMaterial } from '~/types/Material';
  import type { ICutResult } from '~/types/CutResult';
  import type { StrategyComparisonResult } from '~/composables/useOptimizer';
  import DebugVisualization from '~/components/DebugVisualization.vue';
  import DebugStrategyTests from '~/components/DebugStrategyTests.vue';

  // Import utility functions
  import {
    createDefaultParts,
    createPartInstance,
    countPlacedParts,
    expandPartsWithQuantity,
    type Part,
  } from '~/utils/partUtils';
  import {
    groupMaterialsByPattern,
    type MaterialGroup,
  } from '~/utils/materialUtils';
  import { getRandomColor } from '~/utils/visualUtils';

  // Initialize analytics tracking
  const { trackButtonClick, trackOptimization } = useAnalytics();
  import {
    runMultiMaterialOptimization,
    calculateUnplacedParts,
    hasUnplacedParts,
    type UnplacedPartSummary,
  } from '~/utils/optimizationUtils';

  const parts = ref<Part[]>([]);
  const result = ref<ICutResult | IOrientationCompareResult | null>(null);

  const {
    selectedStrategyId,
    availableStrategies,
    optimizationResult,
    isOptimizing,
    error,
    optimize,
    compareAllStrategies,
    comparisonResults,
    isComparingStrategies,
  } = useOptimizer();

  // Initialize material
  const material = ref<IMaterial>({
    id: 'material-1',
    width: 100,
    height: 70,
    children: [],
  });

  // Part management functions
  const addPart = () => {
    const newPart = createPartInstance(parts.value.length);
    parts.value.push(newPart);
  };

  const removePart = (index: number) => {
    parts.value.splice(index, 1);
  };

  // Computed properties
  const groupedMaterials = computed((): MaterialGroup[] => {
    if (!result.value?.usedMaterials) return [];
    return groupMaterialsByPattern(result.value.usedMaterials);
  });

  const hasUnplacedPartsComputed = computed(() => {
    return hasUnplacedParts(parts.value, result.value);
  });

  const unplacedPartsSummary = computed((): UnplacedPartSummary[] => {
    return calculateUnplacedParts(parts.value, result.value);
  });

  // Optimization functions
  const runOptimization = async () => {
    result.value = null;
    comparisonResults.value = [];

    const optimizedResult = await runMultiMaterialOptimization(
      material.value,
      parts.value,
      selectedStrategyId.value,
      optimize
    );

    if (optimizedResult) {
      result.value = optimizedResult;
    } else {
      error.value =
        'No parts could be placed with the current material dimensions. Try increasing the material size or reducing part sizes.';
    }
  };

  const runAllStrategiesComparison = async () => {
    result.value = null;
    const expandedParts = expandPartsWithQuantity(parts.value);
    await compareAllStrategies(material.value, expandedParts);
  };

  const selectComparisonResult = (item: StrategyComparisonResult) => {
    selectedStrategyId.value = item.strategyId;
    optimizationResult.value = item.result;
    result.value = item.result;
  };

  // Debug functionality
  const showDebugTests = ref(false);

  const toggleDebugTests = () => {
    showDebugTests.value = !showDebugTests.value;
  };

  // Initialize default parts on mount
  onMounted(() => {
    parts.value = createDefaultParts();
  });
</script>

<style scoped>
  /* Add any component-specific styles here */
</style>
